import { type Machine, matchMachine } from './machines'
import {
  type PriceBook,
  PROVIDER_IDS,
  type ProviderId,
  type ProviderPrices,
  type StorageClass,
  type Tier,
} from './priceBook'
import type { Period, Resource, Spec } from './spec'

/**
 * Prices a spec on every provider.
 *
 * Everything is worked out per month, the unit providers bill in; an hour and a
 * year are derived from it (`forPeriod`), so the three views can never disagree.
 */

/** What one line costs on one provider, before any discount. */
export interface LineQuote {
  readonly monthly: number
  /** The machine it was priced as, for virtual machines and databases. */
  readonly machine: Machine | null
  /** The provider's product name, where there is one to show. */
  readonly product: string | null
}

export interface ProviderQuote {
  readonly provider: ProviderId
  /** One per resource, in the spec's order; `null` where no machine is large enough. */
  readonly lines: readonly (LineQuote | null)[]
  readonly transfer: LineQuote
  readonly listMonthly: number
  readonly discountMonthly: number
  readonly netMonthly: number
  /** False when a line could not be priced, so the total leaves it out. */
  readonly complete: boolean
}

const MONTHS_PER_YEAR = 12

export function forPeriod(monthly: number, period: Period, hoursPerMonth: number): number {
  if (period === 'hour') return monthly / hoursPerMonth
  if (period === 'year') return monthly * MONTHS_PER_YEAR
  return monthly
}

/** Cost of `quantity` GB through the bands in order: free allowance, then each rate. */
export function tieredCost(quantity: number, tiers: readonly Tier[]): number {
  let left = quantity
  let cost = 0
  for (const tier of tiers) {
    if (left <= 0) break
    const used = tier.gb === null ? left : Math.min(left, tier.gb)
    cost += used * tier.perGb
    left -= used
  }
  return cost
}

export function quoteAll(spec: Spec, book: PriceBook): ProviderQuote[] {
  return PROVIDER_IDS.map((provider) => quoteProvider(spec, provider, book))
}

export function quoteProvider(spec: Spec, provider: ProviderId, book: PriceBook): ProviderQuote {
  const prices = book.providers[provider]
  const stored = storedByClass(spec.resources)
  const lines = spec.resources.map((resource) =>
    quoteLine(resource, prices, book.hoursPerMonth, stored),
  )
  const transfer: LineQuote = {
    monthly:
      spec.transfer.inboundGb * prices.transfer.inboundPerGb +
      tieredCost(spec.transfer.outboundGb, prices.transfer.outbound),
    machine: null,
    product: null,
  }
  const listMonthly = lines.reduce((sum, line) => sum + (line?.monthly ?? 0), transfer.monthly)
  const discountMonthly = (listMonthly * spec.discounts[provider]) / 100
  return {
    provider,
    lines,
    transfer,
    listMonthly,
    discountMonthly,
    netMonthly: listMonthly - discountMonthly,
    complete: lines.every((line) => line !== null),
  }
}

/**
 * Object storage tiers apply to everything stored in a class across the account,
 * so buckets of one class are priced as a pool and each pays its share.
 */
function storedByClass(resources: readonly Resource[]): Record<StorageClass, number> {
  const stored: Record<StorageClass, number> = { standard: 0, infrequent: 0, archive: 0 }
  for (const resource of resources) {
    if (resource.kind === 'bucket') stored[resource.storageClass] += resource.sizeGb
  }
  return stored
}

function quoteLine(
  resource: Resource,
  prices: ProviderPrices,
  hoursPerMonth: number,
  stored: Record<StorageClass, number>,
): LineQuote | null {
  switch (resource.kind) {
    case 'vm': {
      const machine = matchMachine(prices.compute.machines, resource)
      if (!machine) return null
      const licence =
        resource.os === 'windows' ? prices.compute.windowsPerVcpuHour * machine.vcpu : 0
      const hours = (hoursPerMonth * resource.hoursPerDay) / 24
      return {
        monthly: (machine.hourly + licence) * hours * resource.count,
        machine,
        product: null,
      }
    }
    case 'db': {
      const { database } = prices
      const machine = matchMachine(database.machines, resource)
      if (!machine) return null
      const compute = machine.hourly * hoursPerMonth * (resource.ha ? database.haComputeFactor : 1)
      const storage =
        resource.storageGb *
        database.storagePerGbMonth *
        (resource.ha ? database.haStorageFactor : 1)
      return { monthly: (compute + storage) * resource.count, machine, product: database.product }
    }
    case 'disk':
      return {
        monthly: resource.sizeGb * prices.blockStorage.perGbMonth * resource.count,
        machine: null,
        product: prices.blockStorage.product,
      }
    case 'bucket': {
      const { product, tiers } = prices.objectStorage[resource.storageClass]
      const pool = stored[resource.storageClass]
      const share = pool === 0 ? 0 : (tieredCost(pool, tiers) * resource.sizeGb) / pool
      return { monthly: share, machine: null, product }
    }
    case 'lb':
      return {
        monthly: prices.loadBalancer.hourly * hoursPerMonth * resource.count,
        machine: null,
        product: prices.loadBalancer.product,
      }
  }
}

/**
 * Indexes of the lowest values, compared to the cent so that float noise never
 * splits a tie the reader would see as equal. `null`s are never lowest.
 */
export function cheapest(values: readonly (number | null)[]): Set<number> {
  const cents = values.map((value) => (value === null ? null : Math.round(value * 100)))
  const known = cents.filter((value): value is number => value !== null)
  if (known.length === 0) return new Set()
  const lowest = Math.min(...known)
  return new Set(cents.flatMap((value, index) => (value === lowest ? [index] : [])))
}
